import { useMemo, type ReactElement } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ParetoParams } from "@playbookai/widget-schemas";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import { ChartTooltip } from "../../molecules/ChartTooltip/index.js";
import type { WidgetBodyProps } from "../types.js";

export type ParetoAccent = "cyan" | "amber" | "purple";

export interface ParetoPoint {
  label: string;
  value: number;
  cumulative: number;
}

/**
 * Sort the bars descending and attach each bar's cumulative percentage of the
 * total. Pure transform, exported for unit testing. Single pass (O(n)): the
 * running total is a local accumulator, which is fine here because this is a
 * plain helper outside any component/hook render scope (react-hooks/immutability
 * only applies in render scope — that rule is why the original in-component
 * version was extracted into this function).
 */
export function paretoSeries(bars: ParetoParams["bars"]): ParetoPoint[] {
  const sorted = [...bars].sort((a, b) => b.value - a.value);
  const total = sorted.reduce((s, b) => s + b.value, 0) || 1;
  let running = 0;
  return sorted.map((b) => {
    running += b.value;
    return {
      label: b.label,
      value: b.value,
      cumulative: Math.round((running / total) * 100),
    };
  });
}

/**
 * Pareto body — bar + cumulative-% line chart. Used by both `paretoL1`
 * (purple) and `paretoL2` (cyan). Ported from
 * ui-elements/components/solves/workspace/look/ParetoCard.tsx.
 */
export function ParetoBody({
  data,
  accent,
}: {
  data: ParetoParams;
  accent: ParetoAccent;
}): ReactElement {
  const reducedMotion = useReducedMotion();
  const series = useMemo(() => paretoSeries(data.bars), [data.bars]);

  if (series.length === 0) {
    return (
      <div className="flex-1 grid place-items-center text-pb-text-muted text-xs italic">
        No contributors yet. Expand this widget to add data.
      </div>
    );
  }

  const barFill =
    accent === "cyan"
      ? "hsl(var(--pb-cyan))"
      : accent === "amber"
        ? "hsl(var(--pb-amber))"
        : "hsl(var(--pb-purple))";

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-auto">
      <ParetoSummary data={data} />
      <div className="flex justify-between gap-3 text-xs text-pb-text-muted">
        <span>{data.unit || "Value"}</span>
        <span>Cumulative %</span>
      </div>
      <div className="min-h-48 flex-1 overflow-x-auto">
        <div
          style={{ minWidth: Math.max(260, series.length * 80) }}
          className="h-full min-h-48"
        >
          {/* Kept hand-built: recharts chart marks, which no design-system part draws. */}
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={series}
              margin={{ top: 22, right: 0, bottom: 4, left: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--pb-border))"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={<CategoryTick />}
                interval={0}
                textAnchor="middle"
                height={42}
                axisLine={{ stroke: "hsl(var(--pb-border))" }}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12, fill: "hsl(var(--pb-text-muted))" }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: "hsl(var(--pb-text-muted))" }}
                axisLine={false}
                tickLine={false}
                width={40}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                cursor={{ fill: "hsl(var(--pb-border) / 0.4)" }}
                content={
                  <ChartTooltip
                    formatValue={(value, name) =>
                      name === "Cumulative %"
                        ? `${String(value)}%`
                        : String(value)
                    }
                  />
                }
              />
              <Bar
                yAxisId="left"
                dataKey="value"
                name={data.unit || "Value"}
                fill={barFill}
                radius={[3, 3, 0, 0]}
                maxBarSize={42}
                isAnimationActive={!reducedMotion}
              >
                <LabelList
                  dataKey="value"
                  position="top"
                  fontSize={12}
                  fill="hsl(var(--pb-text))"
                />
              </Bar>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cumulative"
                name="Cumulative %"
                unit="%"
                stroke="hsl(var(--pb-text-muted))"
                strokeWidth={1.5}
                dot={{
                  r: 2.5,
                  strokeWidth: 0,
                  fill: "hsl(var(--pb-text-muted))",
                }}
                activeDot={{ r: 4, strokeWidth: 0 }}
                isAnimationActive={!reducedMotion}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export function ParetoL1Widget({
  data,
}: WidgetBodyProps<ParetoParams>): ReactElement {
  return <ParetoBody data={data} accent="purple" />;
}

function CategoryTick({
  x = 0,
  y = 0,
  payload,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
}): ReactElement {
  const label = payload?.value ?? "";
  const words = label.split(/\s+/);
  const first = words.shift() ?? "";
  const rest = words.join(" ");
  const shorten = (text: string): string =>
    text.length > 11 ? `${text.slice(0, 10)}…` : text;
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fontSize={12}
      fill="hsl(var(--pb-text-muted))"
    >
      <title>{label}</title>
      <tspan x={x} dy={14}>
        {shorten(first)}
      </tspan>
      {rest && (
        <tspan x={x} dy={14}>
          {shorten(rest)}
        </tspan>
      )}
    </text>
  );
}

export function ParetoSummary({ data }: { data: ParetoParams }): ReactElement {
  const top = [...data.bars].sort((a, b) => b.value - a.value)[0];
  const total = data.bars.reduce((sum, bar) => sum + bar.value, 0);
  return (
    <div className="shrink-0 space-y-1 text-sm text-pb-text">
      {data.title && (
        <p className="break-words text-xs text-pb-text-muted">{data.title}</p>
      )}
      <p className="break-words">
        {top && total > 0 ? (
          <>
            <strong className="font-semibold">{top.label}</strong> accounts for{" "}
            <strong className="font-semibold">
              {Math.round((top.value / total) * 100)}%
            </strong>{" "}
            of {total.toLocaleString()}
            {data.unit ? ` ${data.unit}` : " total"}.
          </>
        ) : (
          "No contribution to compare yet."
        )}
      </p>
    </div>
  );
}
